import { useMemo, useState } from 'react';
import { Copy, Download, FileText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Markdown } from '@/components/ui/markdown';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';

import { DIRECTION_LABELS, directionLabel } from '../constants';
import { useAgentResults, useUpdateAgentResult } from '../queries';
import type { AgentDirection, AgentResult, AgentResultParams, AgentResultStatus } from '../types';
import { ResultStatusBadge } from './AgentStatusBadge';

const DIRECTION_OPTIONS = Object.entries(DIRECTION_LABELS) as [AgentDirection, string][];
const STATUS_OPTIONS: AgentResultStatus[] = ['draft', 'final', 'posted'];

/** Which slice of results to show. Author-private "mine", team-"shared", or
 *  "all" (founders/admins only — the backend enforces it too). */
type Scope = 'mine' | 'shared' | 'all';

/** Created-date filter granularity. */
type PeriodMode = 'all' | 'quarter' | 'month' | 'date';

/** Turn a period selection into an inclusive-start / exclusive-end ISO range
 *  (local time), or null when no period filter is active. */
function periodRange(
  mode: PeriodMode,
  { year, quarter, month, day }: { year: number; quarter: number; month: string; day: string },
): { after: string; before: string } | null {
  let start: Date;
  let end: Date;
  if (mode === 'quarter') {
    start = new Date(year, (quarter - 1) * 3, 1);
    end = new Date(year, quarter * 3, 1);
  } else if (mode === 'month' && month) {
    const [y, m] = month.split('-').map(Number);
    start = new Date(y, m - 1, 1);
    end = new Date(y, m, 1);
  } else if (mode === 'date' && day) {
    const [y, m, d] = day.split('-').map(Number);
    start = new Date(y, m - 1, d);
    end = new Date(y, m - 1, d + 1);
  } else {
    return null;
  }
  return { after: start.toISOString(), before: end.toISOString() };
}

function slugify(title: string): string {
  const base = title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return base || 'agent-result';
}

async function copyBody(body: string) {
  try {
    await navigator.clipboard.writeText(body);
  } catch {
    // Clipboard may be unavailable (insecure context) — silently ignore.
  }
}

function downloadMarkdown(result: AgentResult) {
  const blob = new Blob([result.body_md], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slugify(result.title || result.public_id)}.md`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Read one result's body_md (rendered) + copy / download, and — for the author
 *  or an admin — a "Share with team" toggle. */
function ResultDialog({
  result,
  canShare,
  onClose,
}: {
  result: AgentResult | null;
  canShare: boolean;
  onClose: () => void;
}) {
  const update = useUpdateAgentResult();
  return (
    <Dialog open={result != null} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{result?.title || 'Untitled result'}</DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="space-y-4">
            <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline" className="font-normal">
                {directionLabel(result.direction)}
              </Badge>
              {result.kind ? <span>{result.kind}</span> : null}
              <ResultStatusBadge status={result.status} />
              {result.is_superseded ? (
                <Badge variant="outline" className="text-muted-foreground font-normal">
                  Superseded
                </Badge>
              ) : null}
              {result.author ? <span>· by {result.author.name}</span> : null}
              <span>· {new Date(result.created).toLocaleString()}</span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => copyBody(result.body_md)}>
                <Copy className="size-4" /> Copy
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadMarkdown(result)}>
                <Download className="size-4" /> Download .md
              </Button>
              {canShare ? (
                <Select
                  value={result.status}
                  disabled={update.isPending}
                  onValueChange={(v) =>
                    update.mutate({ id: result.public_id, input: { status: v as AgentResultStatus } })
                  }
                >
                  <SelectTrigger className="w-32" aria-label="Result status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s === 'draft' ? 'Draft' : s === 'final' ? 'Final' : 'Posted'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              {canShare ? (
                <div className="ml-auto flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Share with team</span>
                  <Switch
                    aria-label="Share with team"
                    checked={result.is_shared}
                    disabled={update.isPending}
                    onCheckedChange={(v) =>
                      update.mutate({ id: result.public_id, input: { is_shared: v } })
                    }
                  />
                </div>
              ) : result.is_shared ? (
                <Badge variant="secondary" className="ml-auto font-normal">
                  Shared
                </Badge>
              ) : null}
            </div>

            {result.body_md.trim() ? (
              <div className="rounded-md border p-4">
                <Markdown>{result.body_md}</Markdown>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">This result has no body.</p>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Results table with Mine / Shared / (All) scope tabs. Reused by the AI-Team
 * "Results" tab (cross-agent) and the agent detail page (`installId` scopes to
 * one agent). Author-private by default; the author or an admin can share.
 */
export function ResultsList({ installId }: { installId?: number }) {
  const role = useSession((s) => s.role);
  const userId = useSession((s) => s.user?.id);
  const isAdmin = role === 'founder' || role === 'super_admin';

  const now = new Date();
  const [scope, setScope] = useState<Scope>('mine');
  const [status, setStatus] = useState<AgentResultStatus | 'all'>('all');
  const [direction, setDirection] = useState<AgentDirection | 'all'>('all');
  const [kind, setKind] = useState<string>('all');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('all');
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [month, setMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
  );
  const [day, setDay] = useState('');
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [selected, setSelected] = useState<AgentResult | null>(null);

  const range = useMemo(
    () => periodRange(periodMode, { year, quarter, month, day }),
    [periodMode, year, quarter, month, day],
  );

  // Everything except `kind` — used both for the table (plus kind) and to
  // populate the kind dropdown with the kinds available under the other filters.
  const baseParams: AgentResultParams = useMemo(() => {
    const p: AgentResultParams = {};
    if (installId) p.install = installId;
    if (scope === 'mine') p.mine = true;
    else if (scope === 'shared') p.shared = true; // 'all' → no scope filter (admin)
    if (status !== 'all') p.status = status;
    if (!installId && direction !== 'all') p.direction = direction;
    if (showSuperseded) p.include_superseded = true;
    if (range) {
      p.created_after = range.after;
      p.created_before = range.before;
    }
    return p;
  }, [installId, scope, status, direction, showSuperseded, range]);

  const params: AgentResultParams = useMemo(
    () => (kind === 'all' ? baseParams : { ...baseParams, kind }),
    [baseParams, kind],
  );

  const resultsQuery = useAgentResults(params);
  const results = resultsQuery.data ?? [];

  // Kind options come from the same filters minus `kind`; when kind is unset the
  // query key equals the table's, so React Query serves one fetch, not two.
  const kindOptionsQuery = useAgentResults(baseParams);
  const kindOptions = useMemo(
    () =>
      Array.from(
        new Set((kindOptionsQuery.data ?? []).map((r) => r.kind).filter(Boolean)),
      ).sort(),
    [kindOptionsQuery.data],
  );

  const canShare = (r: AgentResult) => isAdmin || (r.author?.id != null && r.author.id === userId);

  const tabs: { value: Scope; label: string }[] = [
    { value: 'mine', label: 'Mine' },
    { value: 'shared', label: 'Shared' },
    ...(isAdmin ? [{ value: 'all' as Scope, label: 'All' }] : []),
  ];

  const emptyText =
    scope === 'mine'
      ? 'No results yet — outputs you save appear here.'
      : scope === 'shared'
        ? 'No results have been shared with the team yet.'
        : 'No results yet.';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border p-0.5" role="tablist" aria-label="Results scope">
          {tabs.map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={scope === t.value}
              onClick={() => setScope(t.value)}
              className={cn(
                'cursor-pointer rounded px-3 py-1 text-sm transition-colors',
                scope === t.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        <Select value={status} onValueChange={(v) => setStatus(v as AgentResultStatus | 'all')}>
          <SelectTrigger className="w-36" aria-label="Status filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s === 'draft' ? 'Draft' : s === 'final' ? 'Final' : 'Posted'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-36" aria-label="Kind filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {kindOptions.map((k) => (
              <SelectItem key={k} value={k}>
                {k}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {!installId ? (
          <Select value={direction} onValueChange={(v) => setDirection(v as AgentDirection | 'all')}>
            <SelectTrigger className="w-44" aria-label="Direction filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All directions</SelectItem>
              {DIRECTION_OPTIONS.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}

        <Select value={periodMode} onValueChange={(v) => setPeriodMode(v as PeriodMode)}>
          <SelectTrigger className="w-32" aria-label="Period filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="quarter">By quarter</SelectItem>
            <SelectItem value="month">By month</SelectItem>
            <SelectItem value="date">By date</SelectItem>
          </SelectContent>
        </Select>

        {periodMode === 'quarter' ? (
          <>
            <Input
              type="number"
              aria-label="Year"
              className="w-24"
              value={year}
              onChange={(e) => setYear(Number(e.target.value) || year)}
            />
            <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}>
              <SelectTrigger className="w-24" aria-label="Quarter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4].map((q) => (
                  <SelectItem key={q} value={String(q)}>
                    Q{q}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        ) : null}

        {periodMode === 'month' ? (
          <Input
            type="month"
            aria-label="Month"
            className="w-40"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        ) : null}

        {periodMode === 'date' ? (
          <Input
            type="date"
            aria-label="Date"
            className="w-40"
            value={day}
            onChange={(e) => setDay(e.target.value)}
          />
        ) : null}

        <span className="text-muted-foreground ml-auto flex items-center gap-2 text-sm">
          <Switch
            aria-label="Show superseded"
            checked={showSuperseded}
            onCheckedChange={setShowSuperseded}
          />
          Show superseded
        </span>
      </div>

      {resultsQuery.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : results.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-10 text-center text-sm">
          <FileText className="mx-auto mb-2 size-6" />
          {emptyText}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                {scope !== 'mine' ? <TableHead>Author</TableHead> : null}
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {results.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => setSelected(r)}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className={cn('font-medium', r.is_superseded && 'text-muted-foreground line-through')}>
                        {r.title || 'Untitled'}
                      </span>
                      {r.is_shared ? (
                        <Badge variant="secondary" className="font-normal">
                          Shared
                        </Badge>
                      ) : null}
                      {r.is_superseded ? (
                        <Badge variant="outline" className="text-muted-foreground font-normal">
                          Superseded
                        </Badge>
                      ) : null}
                    </div>
                    {r.kind ? (
                      <div className="text-muted-foreground text-xs">{r.kind}</div>
                    ) : null}
                  </TableCell>
                  {scope !== 'mine' ? (
                    <TableCell className="text-muted-foreground text-sm">
                      {r.author ? (r.author.id === userId ? 'You' : r.author.name) : '—'}
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <ResultStatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {new Date(r.created).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(r);
                      }}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ResultDialog
        result={selected}
        canShare={selected ? canShare(selected) : false}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
